import React, {useEffect, useState} from 'react';
import {
    Create,
    Edit,
    FormDataConsumer,
    SimpleForm,
    TextInput,
    useRecordContext,
    usePermissions,
    useNotify,
    useRedirect,
    useSaveContext,
    useTranslate,
} from 'react-admin';
import {useFormContext} from 'react-hook-form';
import {useLocation} from 'react-router-dom';
import {Alert, AlertTitle, Avatar, Box, Dialog, Typography} from '@mui/material';
import {DeleteOutline, LinkOff} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SectionCard from '../components/statamic/SectionCard';
import StatusDot from '../components/statamic/StatusDot';
import StatamicTextInput from '../components/statamic/StatamicTextInput';
import StatamicSelectInput from '../components/statamic/StatamicSelectInput';
import ToggleRow from '../components/statamic/ToggleRow';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import useDeleteConfirm from '../components/statamic/useDeleteConfirm';
import useTranslatedCountries from '../lib/useTranslatedCountries';
import hasPermission from '../auth/hasPermission';
import {axiosApiInstance} from '../AxiosUtils';
import DynamicMetadataFields from '../components/DynamicMetadataFields';
import SponsorSearchField, {guardianRolePath, sponsorDisplayName} from '../children/SponsorSearchField';

const SALUTATION_CHOICES = (translate) => [
    {id: 'mr',     name: translate('salutation_mr')},
    {id: 'mrs',    name: translate('salutation_mrs')},
    {id: 'family', name: translate('salutation_family')},
];

const PAYMENT_METHOD_CHOICES = (translate) => [
    {id: 'iban',   name: translate('IBAN')},
    {id: 'paypal', name: 'PayPal'},
    {id: 'stripe', name: 'Stripe'},
    {id: 'other',  name: translate('Other')},
];

const PAYMENT_STATUS_CHOICES = (translate) => [
    {id: '',          name: translate('— clear status')},
    {id: 'active',    name: translate('Active')},
    {id: 'pending',   name: translate('Pending')},
    {id: 'failed',    name: translate('Failed')},
    {id: 'cancelled', name: translate('Cancelled')},
    {id: 'paused',    name: translate('Paused')},
];

const LANGUAGE_CHOICES = [
    {id: 'nl', name: 'Nederlands'},
    {id: 'en', name: 'English'},
    {id: 'de', name: 'Deutsch'},
    {id: 'hy', name: 'Հայերեն'},
    {id: 'ru', name: 'Русский'},
];

const ibanFormat = (v) => (!v || v === 'null' || v === 'undefined' || v === 'NULL') ? '' : v;
const ibanParse = (v) => (!v || v === 'null' || v === 'undefined' || !v.trim()) ? null : v;

const SaveAction = ({label, disabled}) => {
    const {handleSubmit} = useFormContext();
    const {save, saving} = useSaveContext();
    return (
        <PrimaryButton variant="primary" disabled={saving || disabled} onClick={handleSubmit(save)}>
            {saving ? 'Saving…' : label}
        </PrimaryButton>
    );
};

const DetachConfirmDialog = ({open, childName, onClose, onConfirm, pending}) => (
    <Dialog
        open={open}
        onClose={pending ? undefined : onClose}
        slotProps={{
            paper: {
                sx: {
                    borderRadius: `${t.radius.lg}px`,
                    border: `1px solid ${t.color.border.default}`,
                    boxShadow: t.shadow.modal,
                    maxWidth: 440,
                },
            },
        }}
    >
        <Box sx={{p: 3}}>
            <Typography
                sx={{
                    fontSize: `${t.type.h3.size}px`,
                    fontWeight: t.type.h3.weight,
                    color: t.color.text.primary,
                }}
            >
                Detach {childName}?
            </Typography>
            <Typography
                sx={{
                    mt: 1,
                    fontSize: `${t.type.small.size}px`,
                    color: t.color.text.secondary,
                    lineHeight: 1.5,
                }}
            >
                The child will no longer be linked to this sponsor. You can re-link them later if needed.
            </Typography>
            <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3}}>
                <PrimaryButton variant="secondary" onClick={onClose} disabled={pending}>
                    Cancel
                </PrimaryButton>
                <PrimaryButton
                    variant="danger"
                    disabled={pending}
                    onClick={onConfirm}
                >
                    {pending ? 'Detaching…' : 'Detach'}
                </PrimaryButton>
            </Box>
        </Box>
    </Dialog>
);

// `buildAttachUpdateBody` and `AttachConflictDialog` lived here — §8e's duplicate-email recipe:
// on a 409, attach the child to the existing guardian, then PUT the typed fields onto them.
// D9 deletes both. The 409 they recovered from told an umbrella admin that an address belongs to a
// sponsor they cannot see, and the PUT wrote details typed for a presumed-new person onto a real
// record (D3). The server now resolves such an address to an attach and returns the ordinary
// guardian model, so there is no conflict left for the client to handle.

const ChildEditCard = ({child, onDetach, canDetach, translate}) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            borderRadius: `${t.radius.md}px`,
            border: `1px solid ${t.color.border.default}`,
        }}
    >
        <Avatar
            src={child.profile_image_thumbnail
                ? `${process.env.REACT_APP_CDN_URL}/${child.profile_image_thumbnail}`
                : undefined}
            sx={{
                width: 40, height: 40,
                bgcolor: t.color.sidebarHover,
                color: t.color.text.secondary,
                border: `1px solid ${t.color.border.hairline}`,
            }}
        />
        <Box sx={{minWidth: 0, flex: 1}}>
            <Typography
                sx={{
                    fontSize: `${t.type.bodyStrong.size}px`,
                    fontWeight: t.type.bodyStrong.weight,
                    color: t.color.text.primary,
                }}
            >
                {child.first_name} {child.last_name}
            </Typography>
        </Box>
        {canDetach && (
            <PrimaryButton
                variant="ghost"
                startIcon={<LinkOff sx={{fontSize: 14}}/>}
                onClick={() => onDetach(child)}
                sx={{fontSize: `${t.type.caption.size}px`, minHeight: 30, px: 1, color: t.color.status.dangerSolid}}
            >
                {translate('Detach from child')}
            </PrimaryButton>
        )}
    </Box>
);

const EmailLockToggle = ({isEditable, onToggle, translate}) => (
    <Box
        onClick={onToggle}
        role="button"
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 0.5,
            cursor: 'pointer',
            fontSize: `${t.type.caption.size}px`,
            color: isEditable ? t.color.status.warningSolid : t.color.text.secondary,
            userSelect: 'none',
        }}
    >
        <input type="checkbox" checked={isEditable} readOnly style={{margin: 0}}/>
        {translate('Make editable')}
    </Box>
);

/**
 * D1 — the sponsor search leads the page, but only on a create that arrived from a child screen.
 *
 * `/guardians/create` is reachable with no child in router state. Rendering a search there would
 * offer to attach a child that does not exist, and the server 400s on a create with no
 * `child_external_id` regardless. W-04 removes the entry point that reaches this state; this guard
 * is the second line of defence. Edit mode is an existing sponsor's own page — its children are
 * managed by the attach/detach cards, not by this field.
 */
export const showsSponsorSearch = ({mode, child}) => mode === 'create' && !!child;

/** D2 — one screen, two submits. Picking a sponsor turns the create into an attach. */
export const addSponsorSubmitMode = ({selectedSponsor}) => (selectedSponsor ? 'attach' : 'create');

/** §8d attach route. `childExternalId` is the child's own UUID, never the numeric v1 path id. */
export const attachChildPath = (rolePath, guardianId, childExternalId) =>
    `/api/v2/${rolePath}/guardians/${guardianId}/child/${childExternalId}`;

/** Form field of the attach branch's sponsor-mail toggle; apart from the create toggle, which defaults off. */
export const ATTACH_SEND_NOTIFICATION_SOURCE = 'attach_send_notification_email';

/**
 * Outcome item 8 — the attach POST's body. The sponsor is mailed unless the admin switched the toggle
 * off; the umbrella and Nofam admin copies go out either way, the project notice only when the
 * umbrella switches it on (server side, item 10). Only the flag is sent, so nothing typed for a new
 * sponsor reaches the picked sponsor's record (D3).
 */
// Contract: nofam-api .ai/docs/integration/nofam-api-v2.md (/api/v2 admin routes, sponsor mail on create and attach)
export const attachRequestBody = (sendNotificationEmail) => ({
    send_notification_email: sendNotificationEmail !== false,
});

/**
 * D4 (revised 2026-09-04) — where a successful create lands: the sponsor's own edit page.
 *
 * The original D4 sent the admin back to the child. That was overturned once the operator accepted
 * the acceptance-9 limitation: under D8 a create can silently resolve to an attach, and the
 * tier-2/tier-3 details the admin typed are then dropped. The child screen offers no way to re-enter
 * them; the sponsor's edit page does, and its "Guardian of" card also shows the child that was just
 * attached — so the admin gets both the confirmation and the fix-up path in one place.
 *
 * <b>It must not depend on which branch the server took.</b> Routing a create to one page and a
 * silent attach to another would make the destination itself announce "this account already
 * existed" — reinstating, as navigation, exactly the oracle D8 removed from the response body. The
 * id below comes from the returned record, which is the created guardian on one branch and the
 * existing one on the other, so both land identically.
 */
export const createSuccessRedirect = (variables, createdRecord) => ['edit', 'guardians', createdRecord?.id];

const HeaderBar = ({mode, record, translate, canDelete, onDelete, saveDisabled,
                     submitMode, onAttach, attaching}) => {
    const salutation = record?.salutation ? translate(record.salutation) : '';
    const fullName = record
        ? [salutation, record.given_name, record.family_name].filter(Boolean).join(' ') || 'Unnamed'
        : null;

    return (
        <PageHeader
            breadcrumb={{label: translate('Sponsors'), to: '/guardians'}}
            title={
                mode === 'create' ? (
                    <Box component="span">{translate('Create sponsor')}</Box>
                ) : (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <StatusDot variant={record?.enabled ? 'success' : 'neutral'}/>
                        <Box component="span">{fullName}</Box>
                    </Box>
                )
            }
            actions={
                <>
                    {/*
                      * D2 — the primary action follows the state of the Sponsor card. With a sponsor
                      * picked this is a plain attach that never enters the form's submit at all, so
                      * nothing typed can reach the chosen sponsor's record (D3).
                      */}
                    {submitMode === 'attach' ? (
                        <PrimaryButton variant="primary" disabled={attaching} onClick={onAttach}>
                            {attaching ? translate('Attaching…') : translate('Attach child')}
                        </PrimaryButton>
                    ) : (
                        <SaveAction
                            label={mode === 'create' ? translate('Create sponsor') : translate('Save changes')}
                            disabled={saveDisabled}
                        />
                    )}
                    {mode === 'edit' && canDelete && (
                        <PrimaryButton
                            variant="danger"
                            startIcon={<DeleteOutline sx={{fontSize: 16}}/>}
                            onClick={onDelete}
                        >
                            {translate('Delete')}
                        </PrimaryButton>
                    )}
                </>
            }
        />
    );
};

const FormBody = ({mode}) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const countries = useTranslatedCountries();
    const location = useLocation();
    const deleteConfirm = useDeleteConfirm();
    const notify = useNotify();
    const redirect = useRedirect();
    const {getValues} = useFormContext();

    const canExportDetails = hasPermission(permissions, ['export_details'], 'guardians');
    const canDetach = hasPermission(permissions, ['detach'], 'guardians');

    const [isEmailEditable, setEmailEditable] = useState(mode === 'create');
    const [newChild, setNewChild] = useState(null);
    // D1/D2 — null until a sponsor is picked from the search; the whole create half collapses once
    // it is set.
    const [selectedSponsor, setSelectedSponsor] = useState(null);
    const [attaching, setAttaching] = useState(false);
    const [attachError, setAttachError] = useState(null);
    const [childrenToDisplay, setChildrenToDisplay] = useState([]);
    const [detachTarget, setDetachTarget] = useState(null);
    const [detachPending, setDetachPending] = useState(false);
    const [detachResult, setDetachResult] = useState(null);

    useEffect(() => {
        const c = location.state?.child;
        if (c) setNewChild(c);
    }, [location]);

    useEffect(() => {
        const base = (record?.children?.length > 0) ? [...record.children] : [];
        if (newChild) base.push(newChild);
        setChildrenToDisplay(base);
    }, [record, newChild]);

    const rolePath = guardianRolePath(permissions);
    const submitMode = addSponsorSubmitMode({selectedSponsor});

    /**
     * D2/D3 — the attach submit. This is a single POST and nothing else: no PUT follows it, so a
     * sponsor picked from the search never has their record written to by details typed for someone
     * presumed new. D4 (revised) — success lands on the sponsor, where the admin can add the scoped
     * payment details for this new relationship and see the child on the "Guardian of" card.
     */
    const handleAttach = () => {
        const childExternalId = newChild?.external_id || newChild?.id;
        if (!selectedSponsor || !childExternalId) return;
        setAttaching(true);
        setAttachError(null);
        axiosApiInstance.post(
            `${process.env.REACT_APP_API_URL}${attachChildPath(rolePath, selectedSponsor.id, childExternalId)}`,
            attachRequestBody(getValues(ATTACH_SEND_NOTIFICATION_SOURCE))
        ).then(() => {
            setAttaching(false);
            notify(translate('Child attached to existing sponsor'), {type: 'success'});
            redirect('edit', 'guardians', selectedSponsor.id);
        }).catch((e) => {
            setAttaching(false);
            setAttachError(e.response?.data?.message || e.message);
        });
    };

    const handleDetach = (child) => {
        setDetachResult(null);
        setDetachTarget(child);
    };

    const confirmDetach = () => {
        setDetachPending(true);
        axiosApiInstance.delete(
            `${process.env.REACT_APP_API_URL}/api/v2/${rolePath}/guardians/${record.id}/child/${detachTarget.external_id}`
        ).then(() => {
            setDetachPending(false);
            setChildrenToDisplay((curr) => curr.filter(c => c.external_id !== detachTarget.external_id));
            setDetachTarget(null);
            setDetachResult('success');
        }).catch(() => {
            setDetachPending(false);
            setDetachTarget(null);
            setDetachResult('error');
        });
    };

    if (mode === 'edit' && !record) return null;

    const salutation = record?.salutation ? translate(record.salutation) : '';
    const fullName = record
        ? [salutation, record.given_name, record.family_name].filter(Boolean).join(' ') || 'Unnamed'
        : null;

    // Plan §2.3/§3 (D5): sponsored_child_count_in_scope === 0 marks a past sponsor —
    // PUT 404s server-side until an attach gives them a current child again. Disable
    // Save here as a second line of defense (List/Show already hide the Edit entry
    // point) rather than letting a direct URL visit hit a 404 on submit. The field is
    // UMBRELLA_ADMIN-only and absent for NOFAM_ADMIN, so this never disables Save for
    // an admin.
    const isPastSponsor = mode === 'edit' && record?.sponsored_child_count_in_scope === 0;

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <HeaderBar
                mode={mode}
                record={record}
                translate={translate}
                canDelete={permissions === 'admin'}
                onDelete={deleteConfirm.openDialog}
                saveDisabled={(mode === 'create' && !newChild) || isPastSponsor}
                submitMode={submitMode}
                onAttach={handleAttach}
                attaching={attaching}
            />

            {record && (
                <DeleteConfirmDialog
                    open={deleteConfirm.open}
                    onClose={deleteConfirm.closeDialog}
                    resource="guardians"
                    record={record}
                    recordLabel={fullName}
                />
            )}

            {isPastSponsor && (
                <Box sx={{px: 3, pt: 3}}>
                    <Alert severity="info" sx={{fontSize: `${t.type.small.size}px`}}>
                        {translate('Sponsorship in this umbrella has ended')} — {translate('Editing is unavailable until this sponsor has a child in your umbrella again')}
                    </Alert>
                </Box>
            )}

            <Box sx={{p: 3, display: 'grid', gridTemplateColumns: '1fr 320px', gap: 2, alignItems: 'start'}}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0}}>
                    {/*
                      * D1 — the Sponsor card leads the page. Empty, it searches; filled, it is a
                      * summary with Change. The whole create half below collapses while it is
                      * filled, so there is only ever one submit on screen (D2).
                      */}
                    {showsSponsorSearch({mode, child: newChild}) && (
                        <SectionCard
                            title={translate('Sponsor')}
                            actions={selectedSponsor ? (
                                <PrimaryButton
                                    variant="secondary"
                                    onClick={() => {
                                        setSelectedSponsor(null);
                                        // The warning describes the attach that just failed for THIS
                                        // sponsor. The Alert lives inside the `selectedSponsor`
                                        // branch, so leaving it set does not show it now — it shows
                                        // it again under the NEXT sponsor picked, before the admin
                                        // has clicked anything, attributing A's failure to B.
                                        setAttachError(null);
                                    }}
                                >
                                    {translate('Change sponsor')}
                                </PrimaryButton>
                            ) : undefined}
                        >
                            {selectedSponsor ? (
                                <>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            p: 1.5,
                                            borderRadius: `${t.radius.md}px`,
                                            border: `1px solid ${t.color.border.default}`,
                                        }}
                                    >
                                        <Avatar sx={{width: 40, height: 40, bgcolor: t.color.sidebarHover}}>
                                            {sponsorDisplayName(selectedSponsor).charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box sx={{minWidth: 0}}>
                                            <Typography sx={{fontSize: `${t.type.bodyStrong.size}px`, fontWeight: 500}}>
                                                {sponsorDisplayName(selectedSponsor)}
                                            </Typography>
                                            <Typography
                                                sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary}}
                                            >
                                                {selectedSponsor.email}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Alert severity="info" sx={{mt: 2, fontSize: `${t.type.small.size}px`}}>
                                        {translate('Will be attached to this sponsor')}
                                    </Alert>
                                    {attachError && (
                                        <Alert severity="warning" sx={{mt: 2, fontSize: `${t.type.small.size}px`}}>
                                            {attachError}
                                        </Alert>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Typography
                                        sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary, mb: 1}}
                                    >
                                        {translate('Already sponsoring with you?')}
                                    </Typography>
                                    <SponsorSearchField onSelect={setSelectedSponsor} translate={translate}/>
                                </>
                            )}
                        </SectionCard>
                    )}

                    {/*
                      * D3 — with a sponsor picked, the create half is hidden and anything typed into
                      * it is dropped. The attach submit is a plain POST, so nothing here can reach
                      * the chosen sponsor's record.
                      */}
                    {selectedSponsor && (
                        <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary}}>
                            {translate('Sponsor details are hidden')}
                        </Typography>
                    )}

                    {/* Outcome item 8 — the sponsor mail on the attach branch, default on (today's
                        behaviour). Its own field, so the create toggle's default (off) does not leak in. */}
                    {selectedSponsor && (
                        <SectionCard title={translate('Email Notification')}>
                            <ToggleRow
                                source={ATTACH_SEND_NOTIFICATION_SOURCE}
                                label={translate('Send welcome email to guardian')}
                                subtitle={translate('Send attach notification helper text')}
                                defaultValue={true}
                            />
                        </SectionCard>
                    )}

                    {!selectedSponsor && (
                    <SectionCard title={translate('Basic Information')}>
                        <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2}}>
                            {mode === 'edit' && (
                                <StatamicSelectInput
                                    source="salutation"
                                    label={translate('Salutation')}
                                    required
                                    choices={SALUTATION_CHOICES(translate)}
                                />
                            )}
                            <StatamicTextInput source="given_name" label={translate('First name')} required/>
                            <StatamicTextInput source="family_name" label={translate('Last name')} required/>
                        </Box>
                        {canExportDetails && (
                            <Box sx={{mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                                <Box>
                                    <StatamicTextInput
                                        source="email"
                                        label={translate('email')}
                                        type="email"
                                        required
                                        helperText={mode === 'edit' ? translate('Edit email helper text') : undefined}
                                        key={isEmailEditable ? 'editable' : 'locked'}
                                    />
                                    {mode === 'edit' && (
                                        <EmailLockToggle
                                            isEditable={isEmailEditable}
                                            onToggle={() => setEmailEditable(v => !v)}
                                            translate={translate}
                                        />
                                    )}
                                </Box>
                                {mode === 'edit' && (
                                    <StatamicTextInput source="phone" label={translate('Phone')}/>
                                )}
                            </Box>
                        )}
                        {mode === 'create' && hasPermission(permissions, ['create'], 'guardians') && (
                            <Box sx={{mt: 2}}>
                                <StatamicTextInput
                                    source="password"
                                    label={translate('Tmp password')}
                                    required
                                    defaultValue="TempWw@Nf"
                                />
                            </Box>
                        )}
                    </SectionCard>
                    )}

                    {/* D5 — the welcome-email toggle of the new-sponsor branch. It collapses with the rest
                        of the create half; the attach branch has its own toggle above (outcome item 8). */}
                    {!selectedSponsor && mode === 'create' && hasPermission(permissions, ['create'], 'guardians') && (
                        <SectionCard title={translate('Email Notification')}>
                            <ToggleRow
                                source="send_notification_email"
                                label={translate('Send welcome email to guardian')}
                                subtitle={translate('Send email notification helper text')}
                                defaultValue={false}
                            />
                            <FormDataConsumer>
                                {({formData}) => formData.send_notification_email && (
                                    <Box sx={{mt: 2}}>
                                        <Alert severity="info" sx={{mb: 2, fontSize: `${t.type.small.size}px`}}>
                                            {translate('Email notification requirements')}
                                        </Alert>
                                        <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2}}>
                                            <StatamicSelectInput
                                                source="salutation"
                                                label={translate('Salutation')}
                                                required
                                                choices={SALUTATION_CHOICES(translate)}
                                            />
                                            <StatamicTextInput source="city" label={translate('City')} required/>
                                            <StatamicSelectInput
                                                source="country"
                                                label={translate('Country')}
                                                required
                                                choices={countries}
                                                defaultValue="NL"
                                            />
                                        </Box>
                                        <Box sx={{mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                                            <StatamicSelectInput
                                                source="language"
                                                label={translate('Language')}
                                                choices={LANGUAGE_CHOICES}
                                                helperText={translate('Auto-detected from umbrella if not set')}
                                            />
                                            <StatamicSelectInput
                                                source="donation_method"
                                                label={translate('Payment method')}
                                                choices={PAYMENT_METHOD_CHOICES(translate)}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </FormDataConsumer>
                        </SectionCard>
                    )}

                    {mode === 'edit' && canExportDetails && (
                        <SectionCard title={translate('Address')}>
                            <Box sx={{mb: 2}}>
                                <StatamicSelectInput
                                    source="country"
                                    label={translate('Country')}
                                    required
                                    choices={countries}
                                    defaultValue="NL"
                                />
                            </Box>
                            <Box sx={{display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 2}}>
                                <StatamicTextInput source="street" label={translate('Street')}/>
                                <StatamicTextInput source="house_number" label={translate('House number')}/>
                                <StatamicTextInput source="postal_code" label={translate('Postal code')}/>
                            </Box>
                            <Box sx={{mt: 2}}>
                                <StatamicTextInput source="city" label={translate('City')}/>
                            </Box>
                        </SectionCard>
                    )}

                    <SectionCard
                        title={mode === 'create' ? translate('Becomes guardian of') : translate('Guardian of')}
                    >
                        {mode === 'create' && !newChild && (
                            <Alert severity="warning" sx={{fontSize: `${t.type.small.size}px`}}>
                                <AlertTitle sx={{fontSize: `${t.type.body.size}px`}}>{translate('Attention')}</AlertTitle>
                                {translate('First select a child')}
                            </Alert>
                        )}
                        {mode === 'create' && newChild && (
                            <>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: 1.5,
                                        borderRadius: `${t.radius.md}px`,
                                        border: `1px solid ${t.color.border.default}`,
                                    }}
                                >
                                    <Avatar
                                        src={newChild.profile_image && newChild.image_ext
                                            ? `${process.env.REACT_APP_CDN_URL}/${newChild.profile_image}.${newChild.image_ext}`
                                            : undefined}
                                        sx={{width: 40, height: 40, bgcolor: t.color.sidebarHover}}
                                    />
                                    <Typography sx={{fontSize: `${t.type.bodyStrong.size}px`, fontWeight: 500}}>
                                        {newChild.first_name} {newChild.last_name}
                                    </Typography>
                                </Box>
                                <TextInput source="child_id" defaultValue={newChild.id} sx={{display: 'none'}}/>
                            </>
                        )}
                        {mode === 'edit' && childrenToDisplay.length > 0 && (
                            <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 1}}>
                                {childrenToDisplay.map(c => (
                                    <ChildEditCard
                                        key={c.external_id}
                                        child={c}
                                        canDetach={canDetach}
                                        translate={translate}
                                        onDetach={handleDetach}
                                    />
                                ))}
                            </Box>
                        )}
                        {mode === 'edit' && childrenToDisplay.length === 0 && (
                            <Typography sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.muted, fontStyle: 'italic', py: 1}}>
                                {translate('No sponsored children')}
                            </Typography>
                        )}
                        {detachResult === 'success' && (
                            <Alert severity="success" sx={{mt: 2, fontSize: `${t.type.small.size}px`}}>
                                <AlertTitle sx={{fontSize: `${t.type.body.size}px`}}>{translate('Child detached title')}</AlertTitle>
                                {translate('Successfully detached child')}
                            </Alert>
                        )}
                        {detachResult === 'error' && (
                            <Alert severity="warning" sx={{mt: 2, fontSize: `${t.type.small.size}px`}}>
                                <AlertTitle sx={{fontSize: `${t.type.body.size}px`}}>{translate('Child detached title')}</AlertTitle>
                                {translate('Failed detaching child')}
                            </Alert>
                        )}
                    </SectionCard>

                    {mode === 'edit' && canExportDetails && (
                        <SectionCard title={translate('Additional Information')}>
                            <DynamicMetadataFields entityType="GUARDIAN" mode="edit"/>
                        </SectionCard>
                    )}
                </Box>

                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, position: 'sticky', top: 16}}>
                    {/*
                      * D2 — create-only, like every card in the left column. The attach submit is a
                      * bodyless POST (D3), so this toggle has nothing to travel on: an admin who
                      * flipped it off before attaching would land on a sponsor who is still enabled,
                      * with no error to explain it. A control that silently discards input is worse
                      * than one that is not offered.
                      */}
                    {!selectedSponsor && (
                    <SectionCard title={translate('Status')}>
                        <ToggleRow
                            source="enabled"
                            label={translate('Enabled')}
                            subtitle={translate('Enabled helper text')}
                            defaultValue={mode === 'create'}
                        />
                    </SectionCard>
                    )}

                    {mode === 'edit' && canExportDetails && (
                        <SectionCard title={translate('Payment information')}>
                            <StatamicSelectInput
                                source="donation_method"
                                label={translate('Payment method')}
                                required
                                choices={PAYMENT_METHOD_CHOICES(translate)}
                            />
                            <Box sx={{mt: 1.5}}>
                                <ToggleRow
                                    source="sponsor_as_company"
                                    label={translate('Sponsor as a company?')}
                                    defaultValue={false}
                                />
                            </Box>
                            <FormDataConsumer>
                                {({formData}) => (
                                    <>
                                        {formData.sponsor_as_company && (
                                            <Box sx={{mt: 1.5}}>
                                                <StatamicTextInput source="company" label={translate('Company')}/>
                                            </Box>
                                        )}
                                        {formData.donation_method === 'iban' && (
                                            <Box sx={{mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5}}>
                                                <StatamicTextInput
                                                    source="iban"
                                                    label={translate('IBAN')}
                                                    format={ibanFormat}
                                                    parse={ibanParse}
                                                />
                                                <StatamicTextInput
                                                    source="direct_debit_start_date"
                                                    label={translate('Direct debit start date')}
                                                    type="date"
                                                />
                                                <StatamicTextInput
                                                    source="direct_debit_reference"
                                                    label={translate('Direct debit reference')}
                                                />
                                            </Box>
                                        )}
                                        {formData.donation_method === 'stripe' && (
                                            <Box sx={{mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5}}>
                                                <StatamicTextInput
                                                    source="stripe_subscription_id"
                                                    label={translate('Stripe Subscription ID')}
                                                    helperText={translate('Usually set by webhook')}
                                                />
                                                <StatamicTextInput
                                                    source="stripe_customer_id"
                                                    label={translate('Stripe Customer ID')}
                                                    helperText={translate('Usually set by webhook')}
                                                />
                                            </Box>
                                        )}
                                    </>
                                )}
                            </FormDataConsumer>
                            <Box sx={{mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5}}>
                                <StatamicSelectInput
                                    source="payment_status"
                                    label={translate('Payment Status')}
                                    choices={PAYMENT_STATUS_CHOICES(translate)}
                                />
                                <StatamicTextInput
                                    source="payment_status_reason"
                                    label={translate('Payment Status Reason')}
                                    multiline
                                    rows={2}
                                />
                            </Box>
                        </SectionCard>
                    )}

                    {mode === 'edit' && canExportDetails && (
                        <SectionCard title={translate('Other info')}>
                            <StatamicTextInput source="note" label={translate('Note')} multiline rows={3}/>
                        </SectionCard>
                    )}
                </Box>
            </Box>

            <DetachConfirmDialog
                open={!!detachTarget}
                childName={detachTarget ? `${detachTarget.first_name} ${detachTarget.last_name}` : ''}
                onClose={() => setDetachTarget(null)}
                onConfirm={confirmDetach}
                pending={detachPending}
            />
        </Box>
    );
};

export const GuardianEditStatamic = (props) => (
    <Edit {...props} actions={false} mutationMode="pessimistic" component={Box} redirect="show">
        <SimpleForm toolbar={false}>
            <FormBody mode="edit"/>
        </SimpleForm>
    </Edit>
);

export const GuardianCreateStatamic = (props) => {
    const notify = useNotify();
    const redirect = useRedirect();

    // D9 — the 409 branch is gone with the recipe it fed. `handleError` itself STAYS: a custom
    // onError suppresses React Admin's default error notification entirely, so without this every
    // failure on the create path would fail silently.
    const handleError = (error) => {
        notify(typeof error === 'string' ? error : (error?.message || 'ra.notification.http_error'), {type: 'error'});
    };

    // D4 (revised 2026-09-04) — land on the SPONSOR returned by the create, never on the child.
    // This has to be onSuccess rather than `<Create redirect>`, which cannot express "derive the
    // destination from the returned record AND notify". `variables` is accepted only to keep the
    // signature React Admin calls and the one the tests pin; see createSuccessRedirect above — it
    // must never be read, because the submitted data is the only input whose shape differs between
    // a create and D8's silent attach, and deriving the route from it would make navigation itself
    // announce that the address already had an account. A custom onSuccess suppresses RA's default
    // success notification the same way onError suppresses the error one, so the notify below is
    // required, not decorative.
    const handleSuccess = (data, variables) => {
        notify('ra.notification.created', {type: 'info', messageArgs: {smart_count: 1}});
        redirect(...createSuccessRedirect(variables, data));
    };

    return (
        <Create
            {...props}
            actions={false}
            mutationMode="pessimistic"
            component={Box}
            redirect={false}
            mutationOptions={{onError: handleError, onSuccess: handleSuccess}}
        >
            <SimpleForm toolbar={false}>
                <FormBody mode="create"/>
            </SimpleForm>
        </Create>
    );
};

export default GuardianEditStatamic;
